import React from 'react';
import { motion } from 'framer-motion';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  
  return (
    <footer className="bg-lamborghini-black py-12 border-t border-lamborghini-gray">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div className="md:col-span-2">
            <motion.h2 
              className="text-2xl font-bold text-lamborghini-yellow mb-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              LAMBORGHINI
            </motion.h2>
            <p className="text-gray-400 mb-6">Experience the extraordinary. The ultimate in luxury and performance.</p>
            <div className="flex space-x-4">
              <SocialIcon name="instagram" />
              <SocialIcon name="facebook" />
              <SocialIcon name="twitter" />
              <SocialIcon name="youtube" />
            </div>
          </div>
          
          <div>
            <h3 className="text-white font-bold mb-4">Models</h3>
            <ul className="space-y-2">
              <FooterLink>Aventador</FooterLink>
              <FooterLink>Huracán</FooterLink>
              <FooterLink>Urus</FooterLink>
              <FooterLink>Limited Series</FooterLink>
            </ul>
          </div>

          <div>
            <h3 className="text-white font-bold mb-4">Contact</h3>
            <ul className="space-y-2">
              <FooterLink>Dealerships</FooterLink>
              <FooterLink>Test Drive</FooterLink>
              <FooterLink>Support</FooterLink>
              <FooterLink>Careers</FooterLink>
            </ul>
          </div>
        </div>
        
        <div className="mt-12 pt-8 border-t border-lamborghini-gray flex flex-col md:flex-row justify-between items-center">
          <p className="text-gray-500 text-sm">© {currentYear} Lamborghini. All rights reserved.</p>
          <div className="flex space-x-6 mt-4 md:mt-0">
            <span className="text-gray-500 text-sm hover:text-white transition-colors duration-300 cursor-pointer">Privacy Policy</span>
            <span className="text-gray-500 text-sm hover:text-white transition-colors duration-300 cursor-pointer">Terms of Service</span>
            <span className="text-gray-500 text-sm hover:text-white transition-colors duration-300 cursor-pointer">Cookies</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

const SocialIcon = ({ name }) => (
  <motion.a 
    href="#" 
    className="w-10 h-10 rounded-full bg-lamborghini-gray flex items-center justify-center text-white"
    whileHover={{ scale: 1.1, backgroundColor: "#FFD700" }}
    whileTap={{ scale: 0.95 }}
  >
    <span className="sr-only">{name}</span>
    {name === "instagram" && <InstagramIcon />}
    {name === "facebook" && <FacebookIcon />}
    {name === "twitter" && <TwitterIcon />}
    {name === "youtube" && <YoutubeIcon />}
  </motion.a>
);

const FooterLink = ({ children }) => (
  <li>
    <motion.a 
      href="#" 
      className="text-gray-400 hover:text-lamborghini-yellow transition-colors duration-300"
      whileHover={{ x: 5 }}
    >
      {children}
    </motion.a>
  </li>
);

// Simple social icons
const InstagramIcon = () => (
  <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
  </svg>
);

const FacebookIcon = () => (
  <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

const TwitterIcon = () => (
  <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
    <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
  </svg>
);

const YoutubeIcon = () => (
  <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
  </svg>
);

export default Footer; 